import { createTheme, alpha } from "@mui/material/styles";

// 🎨 Palette Strategy: "Root Strength" (Warm & Trust)
// Primary: Deep Terracotta (Warmth, Earth, "Vó")
const TERRACOTTA = "#8D4024";
const TERRACOTTA_DARK = "#6B301B";
const TERRACOTTA_LIGHT = "#A65D45";

// Secondary: Sage Green (Growth, Freshness, "Kitchen Herbs")
const SAGE_GREEN = "#4A5D45";
const SAGE_LIGHT = "#7A8F76";

// Neutrals: Linen/Cream (Avoid sterile white)
const LINEN_CREAM = "#FAF5E6";
const WARM_PAPER = "#FFFFFF";
const EARTH_TEXT = "#3E302B"; // Warm Black
const MUTED_EARTH = "#7D6E68"; // Warm Gray

// Dark Mode Palette
const DARK_BG_DEFAULT = "#0d1117";
const DARK_BG_PAPER = "#161b22";
const DARK_TEXT_PRIMARY = "#FFFFFF"; // Pure White for contrast against gradients
const DARK_TEXT_SECONDARY = "#B0B8C4"; // Cool Gray for secondary text

// Alerts
const ALERT_ORANGE = "#D97706";
const DATA_RED = "#D13239";

export const getAuthenticTheme = (mode) => {
    const isDark = mode === 'dark';

    return createTheme({
        palette: {
            mode,
            primary: {
                main: TERRACOTTA,
                light: TERRACOTTA_LIGHT,
                dark: TERRACOTTA_DARK,
                contrastText: "#FFFFFF",
            },
            secondary: {
                main: SAGE_GREEN,
                light: SAGE_LIGHT,
                contrastText: "#FFFFFF",
            },
            error: {
                main: DATA_RED,
            },
            warning: {
                main: ALERT_ORANGE,
            },
            background: {
                default: isDark ? DARK_BG_DEFAULT : LINEN_CREAM,
                paper: isDark ? DARK_BG_PAPER : WARM_PAPER,
            },
            text: {
                primary: isDark ? DARK_TEXT_PRIMARY : EARTH_TEXT,
                secondary: isDark ? DARK_TEXT_SECONDARY : MUTED_EARTH,
            },
            divider: isDark ? alpha(DARK_TEXT_PRIMARY, 0.12) : alpha(EARTH_TEXT, 0.08),
            // Semantic Custom Colors
            semantic: {
                actionRed: TERRACOTTA,
                dataRed: DATA_RED,
                success: SAGE_GREEN,
                warning: ALERT_ORANGE,
                surface: isDark ? DARK_BG_PAPER : LINEN_CREAM,
            }
        },
        // 📐 Geometry & Typography
        typography: {
            fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
            h1: { fontWeight: 700, letterSpacing: "-0.02em", color: isDark ? TERRACOTTA_LIGHT : TERRACOTTA },
            h2: { fontWeight: 700, letterSpacing: "-0.01em", color: isDark ? DARK_TEXT_PRIMARY : EARTH_TEXT },
            h3: { fontWeight: 600, letterSpacing: "-0.01em", color: isDark ? DARK_TEXT_PRIMARY : EARTH_TEXT },
            h4: { fontWeight: 600, color: isDark ? DARK_TEXT_PRIMARY : EARTH_TEXT },
            h5: { fontWeight: 600, color: isDark ? DARK_TEXT_PRIMARY : EARTH_TEXT },
            h6: { fontWeight: 600, color: isDark ? DARK_TEXT_PRIMARY : EARTH_TEXT },
            subtitle1: { fontWeight: 500, color: isDark ? DARK_TEXT_PRIMARY : EARTH_TEXT },
            subtitle2: { fontWeight: 600, color: SAGE_GREEN, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.08em' },
            button: { textTransform: "none", fontWeight: 600, letterSpacing: "0.02em" },
        },
        shape: {
            borderRadius: 12,
        },
        shadows: isDark ? [
            "none",
            "0px 4px 12px rgba(0, 0, 0, 0.4)",
            "0px 8px 24px rgba(0, 0, 0, 0.5)",
            "0px 16px 48px rgba(0, 0, 0, 0.6)",
            ...Array(21).fill("none")
        ] : [
            "none",
            "0px 4px 12px rgba(141, 64, 36, 0.08)",
            "0px 8px 24px rgba(141, 64, 36, 0.12)",
            "0px 16px 48px rgba(141, 64, 36, 0.16)",
            ...Array(21).fill("none")
        ],
        // 🛠 Component Overrides
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: isDark ? DARK_BG_DEFAULT : LINEN_CREAM,
                        color: isDark ? DARK_TEXT_PRIMARY : EARTH_TEXT,
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': {
                            width: '6px',
                            height: '6px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: isDark ? '#444' : '#C8BDB9',
                            borderRadius: '3px',
                        },
                    }
                }
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        padding: "10px 20px",
                        boxShadow: "none",
                        "&:hover": {
                            boxShadow: isDark ? "0px 4px 12px rgba(0,0,0,0.5)" : "0px 4px 12px rgba(141, 64, 36, 0.2)",
                            transform: "translateY(-1px)",
                            transition: "all 0.2s ease-out"
                        }
                    },
                    containedPrimary: {
                        backgroundColor: TERRACOTTA,
                        "&:hover": {
                            backgroundColor: TERRACOTTA_DARK,
                        }
                    },
                    outlinedPrimary: {
                        borderColor: TERRACOTTA,
                        color: TERRACOTTA,
                        "&:hover": {
                            backgroundColor: alpha(TERRACOTTA, 0.05),
                            borderColor: TERRACOTTA_DARK,
                        }
                    }
                }
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 16,
                        boxShadow: isDark ? "0px 4px 20px rgba(0,0,0,0.3)" : "0px 4px 20px rgba(0,0,0,0.03)",
                        border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(141, 64, 36, 0.05)",
                        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                        backgroundColor: isDark ? DARK_BG_PAPER : WARM_PAPER,
                        "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: isDark ? "0px 12px 32px rgba(0,0,0,0.5)" : "0px 12px 32px rgba(141, 64, 36, 0.1)",
                            borderColor: alpha(TERRACOTTA, 0.2)
                        }
                    }
                }
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                    elevation1: {
                        boxShadow: isDark ? "0px 2px 12px rgba(0,0,0,0.2)" : "0px 2px 12px rgba(0,0,0,0.04)"
                    }
                }
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 20,
                        backgroundColor: isDark ? DARK_BG_PAPER : LINEN_CREAM,
                        boxShadow: "0px 32px 64px rgba(0,0,0, 0.5)",
                    }
                }
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        fontWeight: 500,
                    },
                    colorPrimary: {
                        backgroundColor: alpha(TERRACOTTA, 0.1),
                        color: isDark ? TERRACOTTA_LIGHT : TERRACOTTA_DARK,
                    },
                    colorSecondary: {
                        backgroundColor: alpha(SAGE_GREEN, 0.1),
                        color: SAGE_GREEN,
                    }
                }
            }
        }
    });
};
